import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Logo } from "@/components/Logo";
import { ArrowLeft, Calendar, Tag, Share2, BookOpen } from "lucide-react";
import type { BlogPost } from "@shared/schema";
import { getBlogPostBySlug } from "@/lib/blogFirestore";

const CATEGORY_COLORS: Record<string, string> = {
  "App Updates": "bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400",
  "News & Events": "bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400",
  "Business Tips": "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400",
  "General": "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

const CATEGORY_GRADIENTS: Record<string, string> = {
  "App Updates": "from-blue-500 to-blue-700",
  "News & Events": "from-green-500 to-emerald-700",
  "Business Tips": "from-amber-500 to-orange-600",
  "General": "from-slate-400 to-slate-600",
};

function formatDate(dateStr: string | Date | null) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [, setLocation] = useLocation();

  const { data: post, isLoading } = useQuery<BlogPost | null>({
    queryKey: ["firestore/blog_posts/slug", slug],
    queryFn: () => getBlogPostBySlug(slug || ""),
    enabled: !!slug,
  });

  const handleShare = async () => {
    if (navigator.share && post) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt || post.title,
          url: window.location.href,
        });
      } catch {
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      <header className="sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b border-[#8B2121]/10 dark:border-white/10 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button
            onClick={() => setLocation("/blogs")}
            className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 hover:text-[#8B2121] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">Blog</span>
          </button>
          <Logo size="sm" />
          <button
            onClick={handleShare}
            className="p-2 rounded-xl text-slate-400 hover:text-[#8B2121] transition-colors"
            title="Share"
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </header>

      {isLoading ? (
        <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
          <div className="h-56 bg-slate-100 dark:bg-slate-800 rounded-3xl animate-pulse" />
          <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse w-3/4" />
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-4 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
            ))}
          </div>
        </div>
      ) : !post ? (
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <BookOpen className="h-12 w-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
          <h2 className="text-xl font-bold text-slate-600 dark:text-slate-300 mb-2">Post not found</h2>
          <p className="text-slate-400 text-sm mb-6">This post may not exist or has been unpublished.</p>
          <button
            onClick={() => setLocation("/blogs")}
            className="px-5 py-2.5 rounded-2xl text-white text-sm font-bold"
            style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}
          >
            Back to Blog
          </button>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto px-4 py-6 pb-16">
          {post.coverImageUrl ? (
            <img
              src={post.coverImageUrl}
              alt={post.title}
              className="w-full h-56 object-cover rounded-3xl mb-6"
            />
          ) : (
            <div
              className={`h-44 rounded-3xl bg-gradient-to-br ${CATEGORY_GRADIENTS[post.category || "General"] || CATEGORY_GRADIENTS["General"]} flex items-center justify-center mb-6`}
            >
              <BookOpen className="h-12 w-12 text-white/60" />
            </div>
          )}

          <div className="flex items-center gap-2 mb-3">
            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1 ${CATEGORY_COLORS[post.category || "General"] || CATEGORY_COLORS["General"]}`}>
              <Tag className="h-3 w-3" />
              {post.category || "General"}
            </span>
            {post.createdAt && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(post.createdAt)}
              </span>
            )}
          </div>

          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 leading-tight">{post.title}</h1>

          {post.authorName && (
            <p className="text-sm text-slate-400 dark:text-slate-500 mb-6">By {post.authorName}</p>
          )}

          <div
            className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-[#8B2121] prose-img:rounded-2xl text-slate-700 dark:text-slate-300"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>
      )}
    </div>
  );
}
